import type { z } from "zod";
import { LoginStartRequest, LoginStartResponse, SessionResponse } from "./auth.ts";
import { ProductListResponse } from "./catalog.ts";
import { CustomerResponse, CustomerUsersResponse } from "./customer.ts";
import {
  DemoRequest,
  DemoResponse,
  EntitlementListResponse,
  MonthlyLimitRequest,
  MonthlyLimitResponse,
} from "./entitlements.ts";
import { HealthResponse } from "./health.ts";
import { PortalSettings } from "./settings.ts";
import { HistoryQuery, HistoryResponse } from "./history.ts";
import {
  InquiryCreatedResponse,
  InquiryCreateRequest,
  InquiryListQuery,
  InquiryListResponse,
  InquiryUpdateRequest,
  Inquiry,
} from "./inquiries.ts";
import { Order, OrderCancelRequest, OrderCreateRequest, OrderListQuery, OrderListResponse } from "./orders.ts";
import {
  ApproveRequest,
  CorrectionRequest,
  CreditLimitRequest,
  CustomerInstanceRequest,
  CustomerReinstateRequest,
  CustomerSuspendRequest,
  EntitlementRevokeRequest,
  EntitlementTerminateRequest,
  InvoiceRequest,
  KeyRevealResponse,
  KeyRotateRequest,
  LedgerEntryResponse,
  OrderInvoiceRequest,
  OrderStatusRequest,
  StaffCustomerCreateRequest,
  StaffCustomerDetail,
  StaffCustomerListQuery,
  StaffCustomerListResponse,
  StaffCustomerResponse,
  StaffCustomerUser,
  StaffEntitlement,
  StaffLedgerQuery,
  StaffLedgerResponse,
  StaffMonthlyLimitRequest,
  StaffMonthlyLimitResponse,
  StaffOrder,
  StaffOrderCreateRequest,
  StaffOrderListQuery,
  StaffOrderListResponse,
  StaffProduct,
  StaffProductListResponse,
  StaffProductHistoryQuery,
  StaffProductHistoryResponse,
  StaffProductSyncHistoryQuery,
  StaffProductSyncHistoryResponse,
  StaffProductTermsRequest,
  StaffTimelineQuery,
  StaffTimelineResponse,
  StaffUsageReportsQuery,
  StaffUsageReportsResponse,
  StaffUserRoleRequest,
  TaskDoneRequest,
  TaskDoneResponse,
  TaskFailRequest,
  TaskFailResponse,
  TaskListQuery,
  TaskListResponse,
  UsageReportFreezeResponse,
} from "./staff.ts";
import {
  CustomerUsageSummaryResponse,
  UsageItemsQuery,
  UsageItemsResponse,
  UsageLedgerQuery,
  UsageLedgerResponse,
  UsageReportResponse,
  UsageSummaryQuery,
  UsageSummaryResponse,
} from "./usage.ts";

/*
 * The route catalogue of stage "Start" (07 §2.2): method, path and the schemas of query, body
 * and response. The API mounts its handlers from it, the portal's client reads paths and
 * response schemas from it, and the test asserts that the catalogue matches 07. `:id` and
 * `:period` are path parameters. Routes "bei Eintritt, als Skript" and the v1 password routes
 * are not part of it (ADR-062 Hebel 4, ADR-071).
 */

export type RouteSpec = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: `/api/${string}`;
  query?: z.ZodType;
  body?: z.ZodType;
  /** missing on routes that answer with `204 No Content` or a redirect */
  response?: z.ZodType;
};

export const routes = {
  // health and session
  health: { method: "GET", path: "/api/health", response: HealthResponse },
  /** public, before the login: whether guests see the catalogue (ADR-133 point 3) */
  settings: { method: "GET", path: "/api/settings", response: PortalSettings },
  session: { method: "GET", path: "/api/auth/session", response: SessionResponse },
  loginStart: { method: "POST", path: "/api/auth/login/start", body: LoginStartRequest, response: LoginStartResponse },
  /** browser redirect, no JSON: `?code=&state=` in, `302` to returnTo or `/login?error=` out */
  loginCallback: { method: "GET", path: "/api/auth/callback" },
  logout: { method: "POST", path: "/api/auth/logout" },

  // customer side
  products: { method: "GET", path: "/api/products", response: ProductListResponse },
  customer: { method: "GET", path: "/api/customer", response: CustomerResponse },
  customerUsers: { method: "GET", path: "/api/customer/users", response: CustomerUsersResponse },
  orderCreate: { method: "POST", path: "/api/orders", body: OrderCreateRequest, response: Order },
  orderList: { method: "GET", path: "/api/orders", query: OrderListQuery, response: OrderListResponse },
  orderGet: { method: "GET", path: "/api/orders/:id", response: Order },
  orderCancel: { method: "POST", path: "/api/orders/:id/cancel", body: OrderCancelRequest, response: Order },
  entitlementList: { method: "GET", path: "/api/entitlements", response: EntitlementListResponse },
  monthlyLimitSet: {
    method: "PUT",
    path: "/api/entitlements/:id/monthly-limit",
    body: MonthlyLimitRequest,
    response: MonthlyLimitResponse,
  },
  demoRequest: { method: "POST", path: "/api/entitlements/demo", body: DemoRequest, response: DemoResponse },
  usageSummary: {
    method: "GET",
    path: "/api/usage/summary",
    query: UsageSummaryQuery,
    response: CustomerUsageSummaryResponse,
  },
  usageItems: { method: "GET", path: "/api/usage/items", query: UsageItemsQuery, response: UsageItemsResponse },
  usageLedger: { method: "GET", path: "/api/usage/ledger", query: UsageLedgerQuery, response: UsageLedgerResponse },
  usageReport: { method: "GET", path: "/api/usage/reports/:period", response: UsageReportResponse },
  history: { method: "GET", path: "/api/history", query: HistoryQuery, response: HistoryResponse },
  inquiryCreate: {
    method: "POST",
    path: "/api/inquiries",
    body: InquiryCreateRequest,
    response: InquiryCreatedResponse,
  },

  // staff side
  staffCustomerList: {
    method: "GET",
    path: "/api/staff/customers",
    query: StaffCustomerListQuery,
    response: StaffCustomerListResponse,
  },
  /** UC-20, ADR-133 point 4: `201` with the new customer */
  staffCustomerCreate: {
    method: "POST",
    path: "/api/staff/customers",
    body: StaffCustomerCreateRequest,
    response: StaffCustomerResponse,
  },
  staffCustomerGet: { method: "GET", path: "/api/staff/customers/:id", response: StaffCustomerDetail },
  // the customer's usage per product and month for the customer file, from the same core function as
  // the customer's own (`usageSummaryOf`), the team's view with cost and markup (ADR-145, ADR-161 point 3)
  staffCustomerUsageSummary: {
    method: "GET",
    path: "/api/staff/customers/:id/usage/summary",
    query: UsageSummaryQuery,
    response: UsageSummaryResponse,
  },
  // the customer's ledger for the file, newest first, with the balance after each entry (ADR-151)
  staffCustomerLedger: {
    method: "GET",
    path: "/api/staff/customers/:id/ledger",
    query: StaffLedgerQuery,
    response: StaffLedgerResponse,
  },
  staffCustomerTimeline: {
    method: "GET",
    path: "/api/staff/customers/:id/timeline",
    query: StaffTimelineQuery,
    response: StaffTimelineResponse,
  },
  staffCustomerApprove: {
    method: "POST",
    path: "/api/staff/customers/:id/approve",
    body: ApproveRequest,
    response: StaffCustomerResponse,
  },
  staffCreditLimitSet: {
    method: "PUT",
    path: "/api/staff/customers/:id/credit-limit",
    body: CreditLimitRequest,
    response: StaffCustomerResponse,
  },
  staffCustomerSuspend: {
    method: "POST",
    path: "/api/staff/customers/:id/suspend",
    body: CustomerSuspendRequest,
    response: StaffCustomerResponse,
  },
  staffCustomerReinstate: {
    method: "POST",
    path: "/api/staff/customers/:id/reinstate",
    body: CustomerReinstateRequest,
    response: StaffCustomerResponse,
  },
  staffCustomerInstance: {
    method: "PUT",
    path: "/api/staff/customers/:id/instance",
    body: CustomerInstanceRequest,
    response: StaffCustomerResponse,
  },
  staffUserRole: {
    method: "PUT",
    path: "/api/staff/customers/:id/users/:userId/role",
    body: StaffUserRoleRequest,
    response: StaffCustomerUser,
  },
  /** UC-32, ADR-133 point 5: `201` with the new order */
  staffOrderCreate: {
    method: "POST",
    path: "/api/staff/customers/:id/orders",
    body: StaffOrderCreateRequest,
    response: StaffOrder,
  },
  staffOrderList: {
    method: "GET",
    path: "/api/staff/orders",
    query: StaffOrderListQuery,
    response: StaffOrderListResponse,
  },
  /** one order of any customer, as in the list, for the direct link of the mails (ADR-135 addendum) */
  staffOrderGet: { method: "GET", path: "/api/staff/orders/:id", response: StaffOrder },
  staffOrderStatus: {
    method: "PUT",
    path: "/api/staff/orders/:id/status",
    body: OrderStatusRequest,
    response: StaffOrder,
  },
  staffOrderInvoice: {
    method: "PUT",
    path: "/api/staff/orders/:id/invoice",
    body: OrderInvoiceRequest,
    response: StaffOrder,
  },
  staffTaskList: { method: "GET", path: "/api/staff/tasks", query: TaskListQuery, response: TaskListResponse },
  staffTaskDone: {
    method: "POST",
    path: "/api/staff/tasks/:id/done",
    body: TaskDoneRequest,
    response: TaskDoneResponse,
  },
  staffTaskFail: {
    method: "POST",
    path: "/api/staff/tasks/:id/fail",
    body: TaskFailRequest,
    response: TaskFailResponse,
  },
  staffKeyReveal: { method: "POST", path: "/api/staff/entitlements/:id/key/reveal", response: KeyRevealResponse },
  staffKeyRotate: {
    method: "POST",
    path: "/api/staff/entitlements/:id/key/rotate",
    body: KeyRotateRequest,
    response: StaffEntitlement,
  },
  staffEntitlementRevoke: {
    method: "POST",
    path: "/api/staff/entitlements/:id/revoke",
    body: EntitlementRevokeRequest,
    response: StaffEntitlement,
  },
  staffEntitlementTerminate: {
    method: "POST",
    path: "/api/staff/entitlements/:id/terminate",
    body: EntitlementTerminateRequest,
    response: StaffEntitlement,
  },
  staffMonthlyLimitSet: {
    method: "PUT",
    path: "/api/staff/entitlements/:id/monthly-limit",
    body: StaffMonthlyLimitRequest,
    response: StaffMonthlyLimitResponse,
  },
  staffProductList: { method: "GET", path: "/api/staff/products", response: StaffProductListResponse },
  staffProductTerms: {
    method: "PUT",
    path: "/api/staff/products/:id/terms",
    body: StaffProductTermsRequest,
    response: StaffProduct,
  },
  staffProductHistory: {
    method: "GET",
    path: "/api/staff/products/:id/history",
    query: StaffProductHistoryQuery,
    response: StaffProductHistoryResponse,
  },
  // what the catalogue sync wrote to the product (ADR-147 point 4)
  staffProductSyncHistory: {
    method: "GET",
    path: "/api/staff/products/:id/sync-history",
    query: StaffProductSyncHistoryQuery,
    response: StaffProductSyncHistoryResponse,
  },
  /** `204`: an unpublished product nothing uses, its histories stay (ADR-148) */
  staffProductDelete: { method: "DELETE", path: "/api/staff/products/:id" },
  staffUsageReports: {
    method: "GET",
    path: "/api/staff/usage/reports",
    query: StaffUsageReportsQuery,
    response: StaffUsageReportsResponse,
  },
  staffUsageReportFreeze: {
    method: "POST",
    path: "/api/staff/usage/reports/:id/freeze",
    response: UsageReportFreezeResponse,
  },
  staffCorrectionBook: {
    method: "POST",
    path: "/api/staff/ledger/corrections",
    body: CorrectionRequest,
    response: LedgerEntryResponse,
  },
  staffInvoiceBook: {
    method: "POST",
    path: "/api/staff/ledger/invoices",
    body: InvoiceRequest,
    response: LedgerEntryResponse,
  },
  staffInquiryList: {
    method: "GET",
    path: "/api/staff/inquiries",
    query: InquiryListQuery,
    response: InquiryListResponse,
  },
  /** one inquiry, as in the list, for the direct link of the mails (ADR-135 addendum) */
  staffInquiryGet: { method: "GET", path: "/api/staff/inquiries/:id", response: Inquiry },
  staffInquiryUpdate: {
    method: "PUT",
    path: "/api/staff/inquiries/:id",
    body: InquiryUpdateRequest,
    response: Inquiry,
  },
} as const satisfies Record<string, RouteSpec>;

export type RouteName = keyof typeof routes;
export type Routes = typeof routes;

/** Response type of a route, for the portal's typed client. */
export type RouteResponse<N extends RouteName> = Routes[N] extends { response: infer R extends z.ZodType }
  ? z.infer<R>
  : void;

/** Body type of a route. */
export type RouteBody<N extends RouteName> = Routes[N] extends { body: infer B extends z.ZodType } ? z.infer<B> : never;

/** Fills `:param` segments of a path, e.g. `routePath("orderGet", { id })`. */
export function routePath<N extends RouteName>(name: N, params: Record<string, string> = {}): string {
  return routes[name].path.replace(/:([a-zA-Z]+)/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`missing path parameter "${key}" for route "${name}"`);
    return encodeURIComponent(value);
  });
}
